import { useState, useEffect } from 'react';
import {
    DollarSign,
    Search,
    Eye,
    Check,
    X,
    Clock,
    Gift,
    TrendingUp,
    Package,
    RefreshCw,
    Download,
    User
} from 'lucide-react';
import AdminHeader from '@/react-app/components/AdminHeader';
import { api } from '@/shared/api/endpoints';
import { useAuthStore } from '@/shared/stores/authStore';
import type { WithdrawalDataResponse, WithdrawalRequest, TabType, WithdrawalStats } from "@/shared/types";
import { useDebounce } from './hooks/useDebounce';
import { toast } from "react-toastify";


export default function WithdrawalManagement() {
    const [activeTab, setActiveTab] = useState<TabType>('monthly_roi');
    const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
    const [stats, setStats] = useState<WithdrawalStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('pending');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalWithdrawals, setTotalWithdrawals] = useState(0);
    const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalRequest | null>(null);
    const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const { user: authStoreUser, isAuthenticated } = useAuthStore();
    const debouncedSearch = useDebounce(searchTerm, 500)

    if (!isAuthenticated || authStoreUser?.role == "user") {
        window.location.href = '/login';
        return null;
    }

    const withdrawalsPerPage = 20;

    useEffect(() => {
        fetchWithdrawals();
        fetchStats();
    }, [activeTab, currentPage, debouncedSearch, statusFilter]);

    const fetchWithdrawals = async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage.toString(),
                limit: withdrawalsPerPage.toString(),
                search: searchTerm,
                type: activeTab,
                status: statusFilter
            };

            const response = await api.admin.getWithdrawalRequests(params);

            if (response.success && response.data) {
                const data = response.data as WithdrawalDataResponse;
                const tabData = data[activeTab];
                if (!tabData) return;
                setWithdrawals(tabData.items);
                setTotalWithdrawals(tabData.pagination.total || 0);
                // setPagination(data[activeTab].pagination);
            }

        } catch (error) {
            console.error('Error fetching withdrawals:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.admin.getWithdrawalStats();
            if (response.success && response.data) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Error fetching withdrawal stats:', error);
        }
    };

    const processWithdrawal = async (withdrawal: WithdrawalRequest, action: 'approved' | 'rejected') => {
        try {
            setProcessingId(withdrawal.id);

            const params = {
                amount: withdrawal.amount,
                packageId: withdrawal.packageId,
                type: withdrawal.withdrawalType,
                userId: withdrawal.userId,
                action: action,
                withdrawalId: withdrawal.id
            }

            const response = await api.admin.processWithdrawal(params);

            if (response.success) {
                toast.success("Withdrawal Processed");
                // Refresh the list
                fetchWithdrawals();
                fetchStats();
                setShowWithdrawalModal(false);
                setShowRejectionModal(false);
                setRejectionReason('');
            }
        } catch (error) {
            console.error('Error processing withdrawal:', error);
            toast.error("Withdrawal Processing Failed");
        } finally {
            setProcessingId(null);
        }
    };

    const exportWithdrawals = async () => {
        try {
            const params = {
                search: searchTerm,
                type: activeTab,
                status: statusFilter
            };

            const blob = await api.admin.exportWithdrawals(params);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `${activeTab}_withdrawals_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting withdrawals:', error);
        }
    };

    const formatCurrency = (amount: number) => {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'referral_bonus':
                return <Gift className="h-4 w-4 text-purple-500" />;
            case 'monthly_roi':
                return <TrendingUp className="h-4 w-4 text-green-500" />;
            case 'investment_closure':
                return <Package className="h-4 w-4 text-blue-500" />;
            default:
                return <DollarSign className="h-4 w-4 text-gray-500" />;
        }
    };

    const getTypeBadge = (type: string) => {
        const badges = {
            referral_bonus: 'bg-purple-100 text-purple-800',
            monthly_roi: 'bg-green-100 text-green-800',
            investment_closure: 'bg-blue-100 text-blue-800',
        };

        return badges[type as keyof typeof badges] || 'bg-gray-100 text-gray-800';
    };

    const getStatusBadge = (status: string) => {
        const badges = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
            processing: 'bg-blue-100 text-blue-800',
        };

        return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
    };

    const getTabStats = (type: TabType) => {
        if (!stats) return 0;
        switch (type) {
            case 'monthly_roi':
                return stats.monthlyRoiCount;
            case 'referral_bonus':
                return stats.referralBonusCount;
            case 'investment_closure':
                return stats.investmentClosureCount;
            default:
                return 0;
        }
    };

    const getTabTitle = (type: TabType) => {
        switch (type) {
            case 'monthly_roi':
                return 'Monthly ROI';
            case 'referral_bonus':
                return 'Referral Bonus';
            case 'investment_closure':
                return 'Closing Requests';
            default:
                return '';
        }
    };

    const totalPages = Math.ceil(totalWithdrawals / withdrawalsPerPage);

    if (loading && withdrawals.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50">
                <AdminHeader />
                <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading withdrawals...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen dark:bg-gray-900 bg-gray-50">
            <AdminHeader />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Withdrawal Management</h1>
                        <p className="text-gray-600 mt-1">Process user withdrawal requests</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={fetchWithdrawals}
                            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center space-x-2"
                        >
                            <RefreshCw className="h-4 w-4" />
                            <span>Refresh</span>
                        </button>
                        <button
                            onClick={exportWithdrawals}
                            className="bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center space-x-2"
                        >
                            <Download className="h-4 w-4" />
                            <span>Export</span>
                        </button>
                    </div>
                </div>

                {/* Stats Overview */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-600">Pending Requests</h3>
                                <Clock className="h-5 w-5 text-yellow-600" />
                            </div>
                            <p className="text-3xl font-bold text-gray-900">
                                {stats.totalPending.toLocaleString()}
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-600">Total Amount</h3>
                                <DollarSign className="h-5 w-5 text-green-600" />
                            </div>
                            <p className="text-3xl font-bold text-gray-900">
                                {formatCurrency(stats.totalAmount)}
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-600">Monthly ROI</h3>
                                <TrendingUp className="h-5 w-5 text-green-600" />
                            </div>
                            <p className="text-3xl font-bold text-gray-900">
                                {stats.monthlyRoiCount.toLocaleString()}
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium text-gray-600">Referral Bonuses</h3>
                                <Gift className="h-5 w-5 text-purple-600" />
                            </div>
                            <p className="text-3xl font-bold text-gray-900">
                                {stats.referralBonusCount.toLocaleString()}
                            </p>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-8">
                    <div className="border-b border-gray-200">
                        <nav className="flex space-x-8 px-6">
                            {(['monthly_roi', 'referral_bonus', 'investment_closure'] as TabType[]).map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => {
                                        setActiveTab(tab);
                                        setCurrentPage(1);
                                    }}
                                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${activeTab === tab
                                        ? 'border-red-500 text-red-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center space-x-2">
                                        {getTypeIcon(tab)}
                                        <span>{getTabTitle(tab)}</span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${activeTab === tab ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                            {getTabStats(tab)}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Filters */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Search */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search withdrawals..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                            </div>

                            {/* Status Filter */}
                            <div>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                >
                                    {/* <option value="all">All Statuses</option> */}
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="processing">Processing</option>
                                </select>
                            </div>

                            {/* Quick Actions */}
                            {/* <div className="flex space-x-2">
                                <button
                                    onClick={() => setStatusFilter('pending')}
                                    className="flex-1 bg-yellow-100 text-yellow-800 px-3 py-2 rounded-lg text-sm font-medium hover:bg-yellow-200 transition-colors"
                                >
                                    Pending Only
                                </button>
                            </div> */}
                        </div>

                        <div className="mt-4 flex justify-between items-center text-sm text-gray-600">
                            <span>Showing {withdrawals.length} of {totalWithdrawals} {getTabTitle(activeTab).toLowerCase()} withdrawals</span>
                            <span>Page {currentPage} of {totalPages}</span>
                        </div>
                    </div>

                    {/* Withdrawals Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">User</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Package</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Amount</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Status</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Due Date</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {withdrawals.map((withdrawal) => (
                                    <tr key={withdrawal.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                                    <User className="h-5 w-5 text-gray-600" />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900">{withdrawal.userInfo.name}</div>
                                                    <div className="text-sm text-gray-500">{withdrawal.userInfo.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>
                                                <div className="font-medium text-gray-900">{withdrawal.packageInfo.name}</div>
                                                <div className="text-sm text-gray-500">
                                                    Invested: {formatCurrency(withdrawal.packageInfo.investedAmount)}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-lg font-bold text-green-600">
                                                {formatCurrency(withdrawal.amount)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(withdrawal.status)}`}>
                                                {withdrawal.status.charAt(0).toUpperCase() + withdrawal.status.slice(1)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm">
                                                <div className="text-gray-900">
                                                    {new Date(withdrawal.requestDate).toLocaleDateString()}
                                                </div>
                                                <div className="text-gray-500">
                                                    {new Date(withdrawal.requestDate).toLocaleTimeString()}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => {
                                                        setSelectedWithdrawal(withdrawal);
                                                        setShowWithdrawalModal(true);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-700 p-1 rounded transition-colors"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                {withdrawal.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => processWithdrawal(withdrawal, 'approved')}
                                                            disabled={processingId === withdrawal.id}
                                                            className="text-green-600 hover:text-green-700 p-1 rounded transition-colors disabled:opacity-50"
                                                        >
                                                            <Check className="h-4 w-4" />
                                                        </button>
                                                        {/* <button
                                                            onClick={() => {
                                                                setSelectedWithdrawal(withdrawal);
                                                                setShowRejectionModal(true);
                                                            }}
                                                            disabled={processingId === withdrawal.id}
                                                            className="text-red-600 hover:text-red-700 p-1 rounded transition-colors disabled:opacity-50"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button> */}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Empty State */}
                        {withdrawals.length === 0 && !loading && (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    {getTypeIcon(activeTab)}
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    No {getTabTitle(activeTab).toLowerCase()} withdrawals found
                                </h3>
                                <p className="text-gray-500">
                                    {statusFilter === 'pending'
                                        ? `No pending ${getTabTitle(activeTab).toLowerCase()} withdrawals at the moment.`
                                        : `No ${getTabTitle(activeTab).toLowerCase()} withdrawals match your current filters.`
                                    }
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Previous
                                </button>

                                <div className="flex space-x-2">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const page = i + Math.max(1, currentPage - 2);
                                        if (page > totalPages) return null;

                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${currentPage === page
                                                    ? 'bg-red-600 text-white'
                                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Withdrawal Details Modal */}
                {showWithdrawalModal && selectedWithdrawal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900">
                                        {getTabTitle(activeTab)} Withdrawal Details
                                    </h3>
                                    <button
                                        onClick={() => setShowWithdrawalModal(false)}
                                        className="text-gray-400 hover:text-gray-600 p-1"
                                    >
                                        <X className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>

                            <div className="p-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* User Information */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-900 mb-3">User Information</h4>
                                        <div className="space-y-2">
                                            <div>
                                                <span className="text-sm font-medium text-gray-500">Name:</span>
                                                <div className="text-gray-900">{selectedWithdrawal.userInfo.name}</div>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500">Email:</span>
                                                <div className="text-gray-900">{selectedWithdrawal.userInfo.email}</div>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500">Phone:</span>
                                                <div className="text-gray-900">{selectedWithdrawal.userInfo.phone}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Package Information */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-900 mb-3">Package Information</h4>
                                        <div className="space-y-2">
                                            <div>
                                                <span className="text-sm font-medium text-gray-500">Package:</span>
                                                <div className="text-gray-900">{selectedWithdrawal.packageInfo.name}</div>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500">Invested Amount:</span>
                                                <div className="text-gray-900">{formatCurrency(selectedWithdrawal.packageInfo.investedAmount)}</div>
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium text-gray-500">Start Date:</span>
                                                <div className="text-gray-900">{new Date(selectedWithdrawal.packageInfo.startDate ?? "").toLocaleDateString()}</div>
                                            </div>
                                            {selectedWithdrawal.packageInfo.endDate && (
                                                <div>
                                                    <span className="text-sm font-medium text-gray-500">End Date:</span>
                                                    <div className="text-gray-900">{new Date(selectedWithdrawal.packageInfo.endDate).toLocaleDateString()}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Withdrawal Details */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <h4 className="font-semibold text-gray-900 mb-3">Withdrawal Details</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Amount:</span>
                                            <div className="text-2xl font-bold text-green-600">{formatCurrency(selectedWithdrawal.amount)}</div>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Type:</span>
                                            <div className="flex items-center space-x-2 mt-1">
                                                {getTypeIcon(selectedWithdrawal.withdrawalType)}
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeBadge(selectedWithdrawal.withdrawalType)}`}>
                                                    {getTabTitle(activeTab)}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-500">Status:</span>
                                            <div className="mt-1">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedWithdrawal.status)}`}>
                                                    {selectedWithdrawal.status.charAt(0).toUpperCase() + selectedWithdrawal.status.slice(1)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {selectedWithdrawal.description && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                        <div className="bg-gray-100 rounded-lg p-3 text-gray-900">{activeTab}</div>
                                    </div>
                                )}

                                {selectedWithdrawal.notes && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-gray-900">{selectedWithdrawal.notes}</div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                {selectedWithdrawal.status === 'pending' && (
                                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                                        <button
                                            onClick={() => processWithdrawal(selectedWithdrawal, 'approved')}
                                            disabled={processingId === selectedWithdrawal.id}
                                            className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                                        >
                                            <Check className="h-5 w-5" />
                                            <span>Approve {getTabTitle(activeTab)}</span>
                                        </button>
                                        {/* <button
                                            onClick={() => {
                                                setShowWithdrawalModal(false);
                                                setShowRejectionModal(true);
                                            }}
                                            disabled={processingId === selectedWithdrawal.id}
                                            className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                                        >
                                            <X className="h-5 w-5" />
                                            <span>Reject {getTabTitle(activeTab)}</span>
                                        </button> */}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Rejection Modal */}
                {showRejectionModal && selectedWithdrawal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl max-w-md w-full">
                            <div className="p-6 border-b border-gray-200">
                                <h3 className="text-lg font-bold text-gray-900">
                                    Reject {getTabTitle(activeTab)} Withdrawal
                                </h3>
                            </div>

                            <div className="p-6">
                                <p className="text-gray-600 mb-4">
                                    Please provide a reason for rejecting this {getTabTitle(activeTab).toLowerCase()} withdrawal request:
                                </p>
                                <textarea
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                    placeholder="Enter rejection reason..."
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    rows={4}
                                />
                            </div>

                            <div className="flex space-x-4 p-6 border-t border-gray-200">
                                <button
                                    onClick={() => {
                                        setShowRejectionModal(false);
                                        setRejectionReason('');
                                    }}
                                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => processWithdrawal(selectedWithdrawal, 'rejected')}
                                    disabled={!rejectionReason.trim() || processingId === selectedWithdrawal.id}
                                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                                >
                                    Reject
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
